import * as React from "react";

import { ViewportSyncMode } from "@/shared/state/visualization/schema";

import { useEphemeralPanelState } from "../../../WorkspaceCtx";
import { usePlotPanelContext } from "../panelContext/usePlotPanelContext";
import { PlotManager } from "../PlotManager";
import { useViewport } from "../Viewport/useViewport";
import { Viewport } from "../Viewport/viewport";

export function useBroadcastLocalViewport(
  localViewport: Viewport,
  panelId: string,
) {
  // Track the previous viewport to identify when a change has occurred.
  const prevLocalViewport = React.useRef(localViewport);
  const { viewportSyncMode } = usePlotPanelContext();
  const [sharedViewport, setSharedViewport] = useViewport();
  const isInitializedRef = React.useRef(false);
  const [, updateEphemeralState] = useEphemeralPanelState();

  React.useEffect(
    function onLocalViewportChanged() {
      if (prevLocalViewport.current.isOlderThan(localViewport)) {
        if (!isInitializedRef.current) {
          // Mark as initialized - no need to broadcast on first render
          isInitializedRef.current = true;
        } else {
          // Always write viewport to ephemeral state (for sharing/saving)
          updateEphemeralState(panelId, {
            viewport: {
              x: { min: localViewport.x.min, max: localViewport.x.max },
              y: { min: localViewport.y.min, max: localViewport.y.max },
            },
          });

          // Only broadcast to shared viewport if sync is enabled
          if (viewportSyncMode) {
            const combinedViewport = Viewport.combine(
              sharedViewport,
              localViewport,
              viewportSyncMode,
            );
            setSharedViewport(combinedViewport);
          }
        }
      }
      prevLocalViewport.current = localViewport;
    },
    [
      localViewport,
      viewportSyncMode,
      sharedViewport,
      setSharedViewport,
      panelId,
      updateEphemeralState,
    ],
  );
}

export function useOnSyncEnabled(localViewport: Viewport) {
  const { viewportSyncMode } = usePlotPanelContext();
  const [sharedViewport, setSharedViewport] = useViewport();
  // Track the previous viewport to identify when a change has occurred.
  const prevSyncMode = React.useRef<ViewportSyncMode>();
  // Don't synchronize when the component is first mounted.
  const isInitializedRef = React.useRef(false);

  // When this chart starts synchronizing its viewport, sync all other charts' viewports to this one.
  React.useEffect(
    function onViewportSyncEnabled() {
      if (
        viewportSyncMode &&
        viewportSyncMode !== prevSyncMode.current &&
        isInitializedRef.current
      ) {
        setSharedViewport(
          // Because the viewport was just set to synchronize, broadcast
          // this panel's current viewport as the latest viewport.
          Viewport.combine(
            sharedViewport,
            localViewport,
            viewportSyncMode,
            Viewport.latestDebounceId(),
          ),
        );
      }
      isInitializedRef.current = true;
      prevSyncMode.current = viewportSyncMode;
    },
    [localViewport, sharedViewport, viewportSyncMode, setSharedViewport],
  );
}

export function useApplySharedViewport(
  plotManager: PlotManager | null,
  isDragging: boolean,
) {
  const { viewportSyncMode } = usePlotPanelContext();
  const [sharedViewport] = useViewport();
  React.useEffect(
    function applySharedViewport() {
      if (!viewportSyncMode || isDragging) {
        return;
      }
      plotManager?.applyViewport(sharedViewport, viewportSyncMode);
    },
    [sharedViewport, viewportSyncMode, plotManager, isDragging],
  );
}
